import React, { useState, useMemo } from 'react';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  IconButton,
  Badge,
  Menu,
  MenuItem,
  Avatar,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  Collapse,
  Popover,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard,
  People,
  TrendingUp,
  Work,
  Build,
  Receipt,
  Folder,
  CalendarToday,
  Settings,
  ExitToApp,
  ChevronLeft,
  ChevronRight,
  Palette,
  AccessTime,
  LocalShipping,
  ExpandLess,
  ExpandMore,
  EventNote,
  MenuBook,
  BarChart,
  EmojiEvents,
} from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLeads } from '../../contexts/LeadsContext';

const drawerWidth = 260;
const mobileDrawerWidth = 280; // Wider drawer for mobile
const collapsedDrawerWidth = 64;

interface NavigationItem {
  text: string;
  icon: React.ReactElement;
  path: string;
  badge?: number;
  children?: NavigationItem[];
}


interface DashboardLayoutProps {
  children: React.ReactNode;
}

export default function DashboardLayout({ children }: DashboardLayoutProps) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [routerReady, setRouterReady] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState<string[]>([]);
  const [popoverAnchor, setPopoverAnchor] = useState<null | HTMLElement>(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { leads } = useLeads();

  // Debug function to send logs to server
  const sendDebugLog = (message: string, data: any = {}) => {
    const logData = {
      message,
      data,
      timestamp: new Date().toISOString(),
      page: location.pathname,
      isMobile,
      mobileOpen,
      userAgent: navigator.userAgent,
      screenWidth: window.innerWidth,
      screenHeight: window.innerHeight,
    };
    
    fetch('/api/debug-log', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(logData),
    }).catch(() => {});
  };
  
  // Ensure router is ready on mount, especially important for iOS
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setRouterReady(true);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  // Calculate new leads count for current month
  const newLeadsCount = useMemo(() => {
    if (Array.isArray(leads)) {
      const now = new Date();
      const currentMonth = now.getMonth();
      const currentYear = now.getFullYear();
      
      const newLeadsThisMonth = leads.filter(lead => {
        if (lead.status !== 'new') return false;
        
        const leadDate = new Date(lead.created_at);
        return leadDate.getMonth() === currentMonth && 
               leadDate.getFullYear() === currentYear;
      });
      
      return newLeadsThisMonth.length;
    }
    return 0;
  }, [leads]);

  // Dynamic navigation items
  const navigationItems: NavigationItem[] = [
    {
      text: 'Dashboard',
      icon: <Dashboard />,
      path: '/',
    },
    {
      text: 'Contacts',
      icon: <People />,
      path: '/customers',
    },
    {
      text: 'Leads',
      icon: <TrendingUp />,
      path: '/leads',
      badge: newLeadsCount > 0 ? newLeadsCount : undefined,
    },
    {
      text: 'Follow Ups',
      icon: <AccessTime />,
      path: '/follow-up',
    },
    {
      text: 'Projects',
      icon: <Work />,
      path: '/projects',
    },
    {
      text: 'Remedials',
      icon: <Build />,
      path: '/remedials',
    },
    {
      text: 'Remedials Diary',
      icon: <MenuBook />,
      path: '/remedials-diary',
    },
    {
      text: 'File Management',
      icon: <Folder />,
      path: '',
      children: [
        {
          text: 'Invoices',
          icon: <Receipt />,
          path: '/invoices',
        },
        {
          text: 'Documents',
          icon: <Folder />,
          path: '/documents',
        },
      ],
    },
    {
      text: 'Fitters Diary',
      icon: <EventNote />,
      path: '/fitters-diary',
    },
    {
      text: 'Calendar',
      icon: <CalendarToday />,
      path: '/calendar',
    },
    {
      text: 'Won Leads Analytics',
      icon: <BarChart />,
      path: '/analytics/won-leads',
    },
    ...(user?.role === 'admin' ? [{
      text: 'Settings',
      icon: <Settings />,
      path: '/settings',
    }] : []),
  ];

  const comingSoonItems: any[] = [];

  const handleDrawerToggle = () => {
    sendDebugLog('Burger menu clicked', {
      beforeToggle: mobileOpen,
      willBe: !mobileOpen,
      touchEvent: 'handleDrawerToggle'
    });
    setMobileOpen(!mobileOpen);
    
    // Send another log after state update
    setTimeout(() => {
      sendDebugLog('After toggle state update', {
        currentMobileOpen: !mobileOpen
      });
    }, 100);
  };

  const handleCollapseToggle = () => {
    setCollapsed(!collapsed);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleProfileMenuClose();
    navigate('/login');
  };

  // Get page title based on current route
  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/dashboard') return 'Dashboard Overview';
    if (path === '/customers') return 'Contact Management';
    if (path === '/leads') return 'Lead Management';
    if (path.startsWith('/leads/') && path.includes('/edit')) return 'Edit Lead';
    if (path.startsWith('/leads/') && path.includes('/order-build')) return 'Order Build';
    if (path === '/follow-up') return 'Follow Ups';
    if (path === '/projects') return 'Project Management';
    if (path === '/remedials') return 'Remedial Projects';
    if (path === '/on-order') return 'On Order';
    if (path.startsWith('/projects/')) return 'Project Detail';
    if (path === '/invoices') return 'Invoice Management';
    if (path === '/documents') return 'Document Management';
    if (path === '/fitters-diary') return 'Fitters Diary';
    if (path === '/remedials-diary') return 'Remedials Diary';
    if (path === '/analytics/won-leads') return 'Won Leads by Postcode';
    if (path === '/calendar') return 'Calendar & Scheduling';
    if (path === '/settings') return 'System Settings';
    return '';
  };

  const handleNavigation = (path: string, event?: React.MouseEvent | React.TouchEvent, isChildItem?: boolean) => {
    // Special handling for external URL
    if (path.startsWith('http://') || path.startsWith('https://')) {
      window.open(path, '_blank', 'noopener,noreferrer');
      if (isMobile) {
        setMobileOpen(false);
      }
      return;
    }

    sendDebugLog('Menu item clicked', {
      targetPath: path,
      currentPath: location.pathname,
      eventType: event?.type || 'direct'
    });

    // Prevent default to avoid any conflicts
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    // Close expanded menus when clicking a non-child item
    if (!isChildItem) {
      setExpandedMenus([]);
    }

    // Close popover if open
    setPopoverAnchor(null);
    
    
    // Don't navigate to the same path
    if (location.pathname === path) {
      if (isMobile) {
        setMobileOpen(false);
      }
      return;
    }
    
    // Use a small timeout to ensure router is ready on iOS
    const performNavigation = () => {
      try {
        // Check if router is ready
        if (!routerReady || !navigate) {
          window.location.href = path;
          return;
        }
        
        navigate(path);
        
        // Close mobile drawer after navigation
        if (isMobile) {
          setTimeout(() => setMobileOpen(false), 100);
        }
      } catch (error) {
        console.error('[Navigation] Error navigating:', error);
        // Fallback to window.location for iOS compatibility
        window.location.href = path;
      }
    };
    
    // On iOS, use a small delay to ensure touch events complete
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    if (isIOS) {
      requestAnimationFrame(() => {
        setTimeout(performNavigation, 100);
      });
    } else {
      performNavigation();
    }
  };

  const drawer = (
    <Box>
      <Toolbar sx={{ 
        backgroundColor: 'white', 
        color: 'text.primary', 
        py: { xs: 2.5, md: 2 }, 
        position: 'relative',
        minHeight: { xs: 80, md: 64 },
        borderBottom: { xs: '2px solid', md: 'none' },
        borderColor: { xs: 'primary.main', md: 'transparent' }
      }}>
        {!isMobile && (
          <IconButton
            onClick={handleCollapseToggle}
            sx={{
              position: 'absolute',
              right: collapsed ? 8 : 16,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 1,
              backgroundColor: 'background.paper',
              boxShadow: 1,
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
            size="small"
          >
            {collapsed ? <ChevronRight /> : <ChevronLeft />}
          </IconButton>
        )}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          width: '100%', 
          justifyContent: 'center',
          opacity: collapsed ? 0 : 1,
          transition: 'opacity 0.3s ease'
        }}>
          <img 
            src="/gordonslogo.png" 
            alt="Gordon's Makeovers" 
            style={{ 
              maxHeight: collapsed ? '32px' : (isMobile ? '60px' : '50px'), 
              width: 'auto',
              transition: 'max-height 0.3s ease'
            }} 
          />
        </Box>
      </Toolbar>
      <Divider />
      <List>
        {navigationItems.map((item) => (
          item.children ? (
            // Expandable Menu Item
            <Box key={item.text}>
              <ListItem disablePadding>
                <ListItemButton
                  onClick={(e) => {
                    if (collapsed) {
                      // Show popover when collapsed
                      setPopoverAnchor(e.currentTarget);
                    } else {
                      setExpandedMenus(prev =>
                        prev.includes(item.text)
                          ? prev.filter(m => m !== item.text)
                          : [...prev, item.text]
                      );
                    }
                  }}
                  sx={{
                    minHeight: { xs: 64, md: 48 },
                    justifyContent: collapsed ? 'center' : 'initial',
                    px: { xs: 4, md: 2.5 },
                    py: { xs: 2, md: 1 },
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                  title={collapsed ? item.text : undefined}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: 0,
                      mr: collapsed ? 0 : { xs: 3.5, md: 3 },
                      justifyContent: 'center',
                      '& .MuiSvgIcon-root': {
                        fontSize: { xs: '1.75rem', md: '1.5rem' },
                      },
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {!collapsed && (
                    <>
                      <ListItemText
                        primary={item.text}
                        primaryTypographyProps={{
                          fontSize: { xs: '1rem', md: '0.875rem' },
                          fontWeight: { xs: 500, md: 400 },
                        }}
                      />
                      {expandedMenus.includes(item.text) ? <ExpandLess /> : <ExpandMore />}
                    </>
                  )}
                </ListItemButton>
              </ListItem>
              {/* Popover for collapsed sidebar */}
              <Popover
                open={Boolean(popoverAnchor) && collapsed}
                anchorEl={popoverAnchor}
                onClose={() => setPopoverAnchor(null)}
                anchorOrigin={{
                  vertical: 'center',
                  horizontal: 'right',
                }}
                transformOrigin={{
                  vertical: 'center',
                  horizontal: 'left',
                }}
                sx={{ ml: 1 }}
              >
                <List sx={{ py: 0.5 }}>
                  <Typography variant="caption" sx={{ px: 2, py: 0.5, display: 'block', color: 'text.secondary', fontWeight: 600 }}>
                    {item.text}
                  </Typography>
                  {item.children.map((child) => (
                    <ListItem key={child.text} disablePadding>
                      <ListItemButton
                        selected={location.pathname === child.path}
                        onClick={(e) => handleNavigation(child.path, e, true)}
                        sx={{
                          minHeight: 40,
                          px: 2,
                          '&:hover': {
                            backgroundColor: 'action.hover',
                          },
                          '&.Mui-selected': {
                            backgroundColor: 'primary.main',
                            color: 'white',
                            '& .MuiListItemIcon-root': {
                              color: 'white',
                            },
                          },
                        }}
                      >
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          {child.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={child.text}
                          primaryTypographyProps={{ fontSize: '0.875rem' }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Popover>
              <Collapse in={expandedMenus.includes(item.text) && !collapsed} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  {item.children.map((child) => (
                    <ListItem key={child.text} disablePadding>
                      <ListItemButton
                        selected={location.pathname === child.path}
                        onClick={(e) => handleNavigation(child.path, e, true)}
                        sx={{
                          minHeight: { xs: 56, md: 40 },
                          pl: { xs: 6, md: 4 },
                          pr: { xs: 4, md: 2.5 },
                          py: { xs: 1.5, md: 0.75 },
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: 'action.hover',
                          },
                          '&.Mui-selected': {
                            backgroundColor: 'primary.main',
                            color: 'white',
                            '& .MuiListItemIcon-root': {
                              color: 'white',
                            },
                            '&:hover': {
                              backgroundColor: 'primary.dark',
                            },
                          },
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: 0,
                            mr: { xs: 3, md: 2 },
                            justifyContent: 'center',
                            '& .MuiSvgIcon-root': {
                              fontSize: { xs: '1.5rem', md: '1.25rem' },
                            },
                          }}
                        >
                          {child.icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={child.text}
                          primaryTypographyProps={{
                            fontSize: { xs: '0.9rem', md: '0.8rem' },
                            fontWeight: { xs: 400, md: 400 },
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </Box>
          ) : (
            // Regular Menu Item
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                role="button"
                tabIndex={0}
                selected={location.pathname === item.path}
                onClick={(e) => handleNavigation(item.path, e)}
                onTouchEnd={(e) => {
                  e.preventDefault();
                  handleNavigation(item.path, e);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleNavigation(item.path);
                  }
                }}
                sx={{
                  minHeight: { xs: 64, md: 48 },
                  justifyContent: collapsed ? 'center' : 'initial',
                  px: { xs: 4, md: 2.5 },
                  py: { xs: 2, md: 1 },
                  cursor: 'pointer', // Critical for iOS click events
                  WebkitTouchCallout: 'none', // Disable iOS callout
                  WebkitUserSelect: 'none', // Disable text selection
                  userSelect: 'none',
                  touchAction: 'manipulation', // Prevent double-tap zoom
                  WebkitTapHighlightColor: 'rgba(0,0,0,0)', // Remove tap highlight
                  position: 'relative',
                  transition: 'background-color 0.2s ease, transform 0.1s ease',
                  // Use CSS for hover/active states instead of JS
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                  '&:active': {
                    backgroundColor: 'primary.light',
                    transform: 'scale(0.98)',
                  },
                  '&.Mui-selected': {
                    backgroundColor: 'primary.main',
                    color: 'white',
                    '& .MuiListItemIcon-root': {
                      color: 'white',
                    },
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    },
                    // Prevent multiple selections visually
                    pointerEvents: location.pathname === item.path ? 'none' : 'auto',
                  },
                  // iOS specific fixes
                  '@media (pointer: coarse)': {
                    minHeight: 56, // Larger touch target on touch devices
                    '&:active': {
                      backgroundColor: 'primary.light',
                    },
                  },
                }}
                title={collapsed ? item.text : undefined}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: collapsed ? 0 : { xs: 3.5, md: 3 },
                    justifyContent: 'center',
                    '& .MuiSvgIcon-root': {
                      fontSize: { xs: '1.75rem', md: '1.5rem' }, // Bigger icons on mobile
                    },
                  }}
                >
                  {item.badge ? (
                    <Badge
                      badgeContent={item.badge}
                      color="error"
                      variant={collapsed ? "dot" : "standard"}
                    >
                      {item.icon}
                    </Badge>
                  ) : (
                    item.icon
                  )}
                </ListItemIcon>
                {!collapsed && (
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontSize: { xs: '1rem', md: '0.875rem' },
                      fontWeight: { xs: 500, md: 400 },
                    }}
                  />
                )}
              </ListItemButton>
            </ListItem>
          )
        ))}
      </List>
      <Divider sx={{ my: 1 }} />
      <List>
        {comingSoonItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              disabled
              sx={{
                minHeight: { xs: 64, md: 48 },
                justifyContent: collapsed ? 'center' : 'initial',
                px: { xs: 4, md: 2.5 },
                py: { xs: 2, md: 1 },
                opacity: 0.7,
                cursor: 'not-allowed',
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: collapsed ? 0 : 3,
                  justifyContent: 'center',
                  fontSize: { xs: '1.5rem', md: '1.25rem' },
                  color: 'text.secondary',
                }}
              >
                {item.icon}
              </ListItemIcon>
              {!collapsed && (
                <ListItemText
                  primary={item.text}
                  secondary={item.subtext}
                  primaryTypographyProps={{
                    fontSize: { xs: '1rem', md: '0.875rem' },
                    fontWeight: { xs: 500, md: 400 },
                    color: 'text.secondary',
                  }}
                  secondaryTypographyProps={{
                    fontSize: { xs: '0.75rem', md: '0.7rem' },
                    color: 'text.secondary',
                    fontStyle: 'italic',
                  }}
                />
              )}
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${collapsed ? collapsedDrawerWidth : drawerWidth}px)` },
          ml: { md: `${collapsed ? collapsedDrawerWidth : drawerWidth}px` },
          backgroundColor: 'white',
          color: 'text.primary',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            onTouchEnd={(e) => {
              e.preventDefault();
              handleDrawerToggle();
            }}
            sx={{ 
              mr: 2, 
              display: { md: 'none' },
              padding: '16px', // Larger padding for better touch target
              minWidth: '48px',
              minHeight: '48px',
              touchAction: 'manipulation',
              WebkitTapHighlightColor: 'transparent',
              borderRadius: '12px', // Larger border radius for modern look
              '&:active': {
                backgroundColor: 'action.selected',
                transform: 'scale(0.95)',
                transition: 'all 0.1s ease',
              },
              '&:hover': {
                backgroundColor: 'action.hover',
              },
              '& .MuiTouchRipple-root': {
                width: '56px',
                height: '56px'
              }
            }}
            size="large"
          >
            <MenuIcon sx={{ fontSize: '2rem' }} />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <Typography variant="h6" component="h1" sx={{ fontWeight: 'bold', display: { xs: 'none', sm: 'block' } }}>
              {getPageTitle()}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
              Welcome {user?.first_name}
            </Typography>
            <IconButton
              size="large"
              edge="end"
              aria-label="account of current user"
              aria-controls="primary-search-account-menu"
              aria-haspopup="true"
              onClick={handleProfileMenuOpen}
              color="inherit"
              sx={{
                padding: { xs: '12px', md: '8px' },
                minWidth: '48px',
                minHeight: '48px',
                touchAction: 'manipulation',
                WebkitTapHighlightColor: 'transparent',
                '&:active': {
                  transform: 'scale(0.95)',
                  transition: 'transform 0.1s ease',
                },
              }}
            >
              <Avatar 
                sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}
                src={user?.profile_picture || undefined}
              >
                {!user?.profile_picture && user?.first_name?.[0]}{!user?.profile_picture && user?.last_name?.[0]}
              </Avatar>
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { md: collapsed ? collapsedDrawerWidth : drawerWidth }, flexShrink: { md: 0 } }}
        aria-label="navigation folders"
      >
        <Drawer
          variant={isMobile ? 'temporary' : 'permanent'}
          open={isMobile ? mobileOpen : true}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better performance for mobile
            disableScrollLock: true,
            // Improve touch handling
            disableEnforceFocus: false,
            disableAutoFocus: false,
            disableRestoreFocus: false,
            BackdropProps: {
              onClick: handleDrawerToggle,
              onTouchEnd: (e) => {
                e.preventDefault();
                handleDrawerToggle();
              },
            },
          }}
          // Enhanced swipe handling for mobile (handled by MUI automatically)
          sx={{
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: isMobile ? mobileDrawerWidth : (collapsed ? collapsedDrawerWidth : drawerWidth),
              transition: theme.transitions.create(['width', 'transform'], {
                easing: theme.transitions.easing.easeOut,
                duration: theme.transitions.duration.shorter,
              }),
              overflowX: 'hidden',
              backgroundColor: { xs: '#fafafa', md: 'background.paper' },
              // Improve scrolling on mobile
              overflowY: 'auto',
              WebkitOverflowScrolling: 'touch',
              // Better touch scrolling
              '&::-webkit-scrollbar': {
                width: '6px',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(0,0,0,0.2)',
                borderRadius: '3px',
              },
            },
            '& .MuiBackdrop-root': {
              touchAction: 'manipulation', // Allow touch actions
              backgroundColor: 'rgba(0, 0, 0, 0.6)', // Darker backdrop
              // Smooth backdrop transition
              transition: theme.transitions.create('opacity', {
                easing: theme.transitions.easing.easeOut,
                duration: theme.transitions.duration.shorter,
              }),
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 1, sm: 2, md: 3 },
          width: { xs: '100%', md: `calc(100% - ${collapsed ? collapsedDrawerWidth : drawerWidth}px)` },
          minHeight: '100vh',
          backgroundColor: 'background.default',
          transition: theme.transitions.create(['width', 'margin'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
          overflowX: 'hidden',
          maxWidth: '100vw'
        }}
      >
        <Toolbar />
        <Box sx={{ width: '100%', overflowX: 'hidden' }}>
          {children}
        </Box>
      </Box>
      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        onClick={handleProfileMenuClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            mt: 1.5,
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              ml: -0.5,
              mr: 1,
            },
            '&:before': {
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleProfileMenuClose}>
          <Avatar 
            sx={{ bgcolor: 'primary.main' }}
            src={user?.profile_picture || undefined}
          >
            {!user?.profile_picture && user?.first_name?.[0]}{!user?.profile_picture && user?.last_name?.[0]}
          </Avatar>
          <Box>
            <Typography variant="body2" fontWeight="bold">
              {user?.first_name} {user?.last_name}
            </Typography>
            <Typography variant="caption" color="textSecondary">
              {user?.role}
            </Typography>
          </Box>
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => handleNavigation('/settings')}>
          <ListItemIcon>
            <Settings fontSize="small" />
          </ListItemIcon>
          Settings
        </MenuItem>
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <ExitToApp fontSize="small" />
          </ListItemIcon>
          Logout
        </MenuItem>
      </Menu>
    </Box>
  );
}